<template>
  <div
    class="full-width stream-insights-chart tw:h-full"
    :class="{
      'tw:mb-auto': alignTop,
    }"
    data-testid="stream-insights-chart"
  >
    <StreamInsightsChartEmpty
      v-if="displayEmptyState"
      :chart-config="chartConfig"
      :chart-detail="chartDetail"
      data-testid="stream-insights-chart-empty"
      :hide-button="isEmptyButtonHidden || isPublic"
      @empty-clicked="emit('empty-clicked')"
    />

    <div
      v-else-if="showGoogleAnalyticsError"
      class="tw:flex tw:items-center tw:justify-center tw:h-full"
    >
      <div
        class="tw:mx-auto tw:max-w-md tw:text-left tw:text-sm tw:text-red-500"
        data-testid="google-analytics-error-message"
      >
        <BaseAlertMessage type="warning">
          {{ getLocaleText("instant_insights_errors.google_analytics") }}
        </BaseAlertMessage>
      </div>
    </div>

    <ChartLibraryChart
      v-else
      ref="chart"
      :chart-config="chartConfig"
      :chart-data="chartData"
      :error="error"
      :intersect-once="intersectOnce"
      :isolate-layout="isolateLayout"
      :loading="loading"
      @chart-clicked="emit('chart-clicked', $event, chartData)"
      @chart:render="emit('chart:render', $event)"
      @close-popup="emit('close-popup', $event)"
      @intersection="handleIntersection"
      @reload="reload"
    >
      <template #before>
        <slot name="before" />

        <ChartSocialAccountLabel
          v-if="socialAccount"
          :label="socialAccount.label"
          :platform-icon="socialAccount.platformIcon"
        />
      </template>

      <template
        v-if="$slots.loading"
        #loading
      >
        <slot name="loading" />
      </template>
    </ChartLibraryChart>

    <div v-if="displayPaginationSection">
      <div class="bottom-border-divider" />

      <BasePagination
        class="q-pa-sm float-right"
        hide-page-size-options
        :max-pages="MAX_NUMBER_OF_PAGES"
        :pagination="pagination"
        @pagination-changed="onPaginationChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Chart } from "chart.js";
import { cloneDeep, debounce as debounceHandler, isEqual } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { getLocale, getLocaleText } from "shared/boot/i18n";
import BaseAlertMessage from "shared/components/base/BaseAlertMessage";
import BasePagination, {
  type BasePaginationData,
} from "shared/components/base/BasePagination";
import type { MultiHeaderWidgetOption } from "shared/components/widgets/MultiHeaderWidget.vue";
import useCachedResults from "shared/composables/useCachedResults";
import useTeamPicker from "shared/composables/useTeamPicker";
import { MINUTE_IN_MILLISECONDS } from "shared/constants";
import { serializeAdvancedFilters } from "shared/helpers/advancedFilters";
import DateRange from "shared/helpers/DateRange";
import type { MediumName } from "shared/helpers/media";
import { isSameStream } from "shared/helpers/stream";
import transformKeys from "shared/helpers/transformKeys";
import { type AIVisibilityProjectSource } from "shared/modules/AIVisibility/types";
import ChartLibraryChart from "shared/modules/Charts/components/ChartLibraryChart/ChartLibraryChart.vue";
import ChartSocialAccountLabel from "shared/modules/Charts/components/ChartSocialAccountLabel/ChartSocialAccountLabel.vue";
import StreamInsightsChartEmpty from "shared/modules/Charts/components/StreamInsightsChartEmpty/StreamInsightsChartEmpty.vue";
import { useChartInterval } from "shared/modules/Charts/composables/useChartInterval";
import { useChartSocialAccount } from "shared/modules/Charts/composables/useChartSocialAccount";
import { normalizeChartSetting } from "shared/modules/Charts/helpers/charts";
import {
  CHART_AXIS_INTERVALS,
  ChartAxisInterval,
  type InsightsMetric,
  type LinkedStreams,
  type SavedChartSetting,
} from "shared/modules/Charts/types";
import type { ChartConfig } from "shared/modules/Charts/types/chartConfig";
import type { ChartClickEvent } from "shared/modules/Charts/types/chartInteractions";
import type {
  ChartLibraryData,
  ChartLibraryProps,
  ChartLibraryRequestParams,
  ChartLibraryTableDataColumn,
  KeyQueryParams,
} from "shared/modules/Charts/types/data";
import { isChartLibraryData } from "shared/modules/Charts/types/data/guards";
import {
  hasIntentionalEmptyMentionStreams,
  isChartSettingWithAIVisibility,
  isChartSettingWithAnalyticsIntegration,
  isChartSettingWithContactKeys,
  isChartSettingWithExportOptions,
  isChartSettingWithInsightsMetrics,
  isChartSettingWithInstagramHandleSelection,
  isChartSettingWithKeyMessages,
  isChartSettingWithLinkedSocialStreams,
  isChartSettingWithMediaReleases,
  isChartSettingWithMentionStreamsAndSocialStreams,
  isChartSettingWithOnlyAIVisibility,
  isChartSettingWithOptionalLinkedSocialStreams,
  isChartSettingWithOutreachActivityFilters,
  isChartSettingWithPostSelection,
  isChartSettingWithSocialAccountSelection,
  isChartSettingWithSocialEngagePerformanceMetrics,
  isChartSettingWithSortBy,
  isChartSettingWithSovMetricType,
  isChartSettingWithStockSymbol,
  isChartSettingWithStreamComparison,
  isChartSettingWithStreamsAndMediaTypes,
} from "shared/modules/Charts/types/guards";
import {
  IsPublicInsightsSymbol,
  PublicAccessKeySymbol,
  SelectedDashboardSymbol,
} from "shared/modules/Charts/types/symbols";
import createInsightsService from "shared/services/api/InsightsServiceFactory";
import { isAbortError } from "shared/services/api/isAbortError";
import features from "shared/services/features";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";
import type { Nullable, Stream, Uuid } from "shared/types";
import type { MentionType } from "shared/types/mentions";

export interface StreamInsightsChartProps {
  authors?: string[];
  cacheToken?: Uuid;
  chartConfig: ChartConfig;
  chartDetail?: boolean;
  chartSetting: SavedChartSetting;
  collapseSyndicated?: boolean;
  debounce?: number;
  disableAnimation?: boolean;
  disableLoading?: boolean;
  displayPagination?: boolean;
  filterSetId?: Nullable<number>;
  hasAiVisibilityProject?: boolean;
  initialChartData?: Nullable<ChartLibraryProps>;
  instantInsightsReportId?: number | string;
  intersectOnce?: boolean;
  isEmptyButtonHidden?: boolean;
  isolateLayout?: boolean;
  isTable?: boolean;
  media?: MediumName[];
  mediaTypes?: MentionType[];
  range: DateRange;
  selectedMediaRelease?: MultiHeaderWidgetOption;
  selectedStream?: Nullable<Stream>;
  skipCache?: boolean;
  streams?: Stream[];
}

const CACHE_TTL = 5 * MINUTE_IN_MILLISECONDS;

const PAGE_SIZE = 7;

const MAX_NUMBER_OF_PAGES = 4;

const selectedDashboard = inject(SelectedDashboardSymbol, null);
const isPublic = inject<boolean>(IsPublicInsightsSymbol, false);
const publicAccessKey = inject<string>(PublicAccessKeySymbol, "");

const props = withDefaults(defineProps<StreamInsightsChartProps>(), {
  authors: () => [],
  cacheToken: undefined,
  debounce: 500,
  filterSetId: null,
  initialChartData: null,
  media: () => [],
  mediaTypes: () => [],
  selectedMediaRelease: () => ({}) as MultiHeaderWidgetOption,
  selectedStream: () => ({}) as Stream,
  streams: () => [],
  instantInsightsReportId: "",
});

const emit = defineEmits<{
  "chart-clicked": [ChartClickEvent, ChartLibraryProps];
  "chart:render": [Chart];
  "close-popup": [InstanceType<typeof ChartLibraryChart>];
  "data-loaded": [ChartLibraryProps];
  "empty-clicked": [];
  intersection: [boolean];
}>();

const insightsService = createInsightsService({
  publicAccessKey: isPublic ? publicAccessKey : undefined,
});

const { withCachedResults, makeCacheKey } =
  useCachedResults<ReturnType<typeof insightsService.streamInsights>>(
    CACHE_TTL
  );

const abortController = ref<AbortController>();

const globalStore = useGlobalStore();

const { analyticsIntegration } = storeToRefs(globalStore);

const { getIntervalPermissions } = useChartInterval();

const { selectedTeamPreference } = useTeamPicker();

const userStore = useUserStore();

const { betaEnabled, currentUser } = storeToRefs(userStore);

const chart = ref<InstanceType<typeof ChartLibraryChart>>();
const loading = ref(!props.initialChartData);
const loaded = ref(Boolean(props.initialChartData));
const error = ref(false);
const isIntersecting = ref(false);
const chartData = ref<ChartLibraryProps>(props.initialChartData || {});
const copyChartData = ref<ChartLibraryProps>({});
const firstLoad = ref(!props.initialChartData);

const pagination = ref<BasePaginationData>({
  page: 1,
  pageCount: 0,
  pageSize: PAGE_SIZE,
});

const numberOfRecords = ref(0);
const isUnmounted = ref(false);

const chartSettingsStreamIds = computed<number[]>(() => {
  if (isChartSettingWithStreamsAndMediaTypes(props.chartSetting)) {
    return props.chartSetting.stream_ids || [];
  }

  return [];
});

const hasIntentionalEmptyStreams = computed<boolean>(() =>
  hasIntentionalEmptyMentionStreams(props.chartSetting)
);

const selectedStreamId = computed<number>(() => {
  if (hasIntentionalEmptyStreams.value) {
    return 0;
  }

  if (props.selectedStream?.id) {
    return Number(props.selectedStream?.id);
  }

  if (chartSettingsStreamIds.value.length) {
    return chartSettingsStreamIds.value[0];
  }

  if (selectedDashboard?.value?.streamIds?.length) {
    return selectedDashboard.value.streamIds[0];
  }

  // Fallback to 0 if no stream is selected or available
  // In the service layer, we handle the case where stream_id is 0
  return 0;
});

const selectedStreamComputed = computed<Stream>(
  () =>
    props.streams.find(
      (stream) => Number(stream.id) === selectedStreamId.value
    ) || ({} as Stream)
);

const comparisonStreamIds = computed<number[]>(() => {
  if (hasIntentionalEmptyStreams.value) {
    return [];
  }

  if (chartSettingsStreamIds.value.length) {
    return chartSettingsStreamIds.value.filter(
      (id) => id !== selectedStreamId.value
    );
  }

  return props.streams.reduce((acc, { id }) => {
    if (Number(id) !== Number(props.selectedStream?.id)) {
      acc.push(Number(id));
    }

    return acc;
  }, [] as number[]);
});

const displayPaginationSection = computed<boolean>(
  () =>
    !loading.value &&
    !error.value &&
    props.displayPagination &&
    pagination.value.pageCount > 1
);

const chartQueries = computed<KeyQueryParams[]>(() => {
  if (isChartSettingWithKeyMessages(props.chartSetting)) {
    return props.chartSetting.queries.filter(
      (query) => query.stream_id === props.selectedStream?.id
    );
  }

  return [];
});

const linkedStreamIds = computed<LinkedStreams>(() => {
  if (hasIntentionalEmptyStreams.value) {
    return {};
  }

  if (
    isChartSettingWithLinkedSocialStreams(props.chartSetting) ||
    isChartSettingWithOptionalLinkedSocialStreams(props.chartSetting)
  ) {
    return props.chartSetting.linked_stream_ids || {};
  }

  return {};
});

const normalizedChartSetting = computed(() =>
  normalizeChartSetting(props.chartSetting)
);

const mediaReleaseIds = computed<number[]>(() => {
  const chartSetting = normalizedChartSetting.value;

  if (isChartSettingWithMediaReleases(chartSetting)) {
    return chartSetting.media_release_ids ?? [];
  }

  return [];
});

const insightsMetrics = computed<InsightsMetric[]>(() => {
  if (isChartSettingWithInsightsMetrics(props.chartSetting)) {
    return props.chartSetting.insights_metrics;
  }

  return [];
});

const llmModels = computed<AIVisibilityProjectSource[]>(() => {
  if (
    isChartSettingWithAIVisibility(props.chartSetting) &&
    props.chartSetting.llm_models?.length
  ) {
    return props.chartSetting.llm_models;
  }

  return [];
});

const llmEntities = computed<string[]>(() => {
  if (
    isChartSettingWithAIVisibility(props.chartSetting) &&
    props.chartSetting.llm_entities?.length
  ) {
    return props.chartSetting.llm_entities.map((entity) => entity.name);
  }

  return [];
});

const isAnalyticsIntegrated = computed<boolean>(() =>
  Boolean(analyticsIntegration.value && analyticsIntegration.value.isIntegrated)
);

// charts rendered for a single social account name that account on the chart,
// so it can be identified without opening the chart's settings
const socialAccountId = computed<Nullable<string>>(() => {
  if (isPublic || props.chartConfig.allowsMultipleSocialAccounts !== false) {
    return null;
  }

  if (!isChartSettingWithSocialAccountSelection(props.chartSetting)) {
    return null;
  }

  return props.chartSetting.organisation_social_integration_ids?.[0] ?? null;
});

const { socialAccount } = useChartSocialAccount(socialAccountId);

const displayEmptyState = computed<boolean>(() => {
  if (loading.value) {
    return false;
  }

  if (!props.chartConfig.emptyState) {
    return false;
  }

  if (isChartSettingWithKeyMessages(props.chartSetting)) {
    return !chartQueries.value.length;
  }

  if (isChartSettingWithStockSymbol(props.chartSetting)) {
    return !props.chartSetting.stock_symbol;
  }

  if (isChartSettingWithLinkedSocialStreams(props.chartSetting)) {
    return Boolean(
      props.selectedStream?.id &&
      !linkedStreamIds.value[props.selectedStream.id] &&
      !selectedStreamComputed.value.linked_stream_id
    );
  }

  if (isChartSettingWithMediaReleases(props.chartSetting)) {
    if (hasIntentionalEmptyStreams.value) {
      return false;
    }

    return !mediaReleaseIds.value.length;
  }

  if (props.chartSetting.requires_analytics_integration) {
    return (
      !isPublic &&
      !isAnalyticsIntegrated.value &&
      !props.chartSetting.analytics_integration_target
    );
  }

  if (props.chartConfig.requiresSocialPostSelection) {
    return !props.chartSetting.post_ids?.length;
  }

  if (isChartSettingWithOnlyAIVisibility(props.chartSetting)) {
    return !props.hasAiVisibilityProject;
  }

  return false;
});

const showGoogleAnalyticsError = computed<boolean>(() => {
  if (loading.value) {
    return false;
  }

  if (props.chartSetting.requires_analytics_integration) {
    const data = chartData.value.data as ChartLibraryData;

    return (
      (!isAnalyticsIntegrated.value &&
        !isPublic &&
        !props.chartSetting.analytics_integration_target) ||
      Boolean(data?.analytics_error)
    );
  }

  return false;
});

const alignTop = computed<boolean>(() =>
  Boolean(
    props.isTable &&
    !loading.value &&
    !displayEmptyState.value &&
    !error.value &&
    numberOfRecords.value > 0
  )
);

const shouldSkipCache = computed<boolean>(
  () => props.skipCache || firstLoad.value
);

function onPaginationChange(updatedPagination: BasePaginationData): void {
  pagination.value = updatedPagination;
}

function handleIntersection(value: boolean): void {
  isIntersecting.value = value;
  emit("intersection", isIntersecting.value);
}

function getChartQueries(): KeyQueryParams[] {
  if (!isChartSettingWithKeyMessages(props.chartSetting)) return [];

  if (!props.chartSetting.queries) {
    return [];
  }

  return props.chartSetting.queries.filter(
    (query) => query.stream_id === props.selectedStream?.id
  );
}

const range = computed<DateRange>(
  () => props.chartSetting.range || props.range
);

const relativeRange = computed<Nullable<DateRange>>(() => {
  if (range.value?.range?.label) {
    return DateRange.rangeForLabel(range.value.range.label);
  }

  return null;
});

const absoluteRange = computed<DateRange>(() => {
  const { before, after } = range.value;

  return new DateRange({ before, after } as DateRange);
});

function getChartInterval(
  dateRange: DateRange,
  chartInterval?: ChartAxisInterval
): ChartAxisInterval {
  const intervalPermissions = getIntervalPermissions(dateRange);

  const allowedIntervals: ChartAxisInterval[] = CHART_AXIS_INTERVALS.filter(
    (interval) => intervalPermissions[interval]
  );

  const requestedOrDefaultInterval = chartInterval || ChartAxisInterval.Day;

  const isRequestedAllowed = allowedIntervals.includes(
    requestedOrDefaultInterval
  );

  if (isRequestedAllowed) {
    return requestedOrDefaultInterval;
  }

  return allowedIntervals.length > 0
    ? allowedIntervals[allowedIntervals.length - 1]
    : ChartAxisInterval.Day;
}

function prepareParams(): ChartLibraryRequestParams {
  const chartRange = relativeRange.value || absoluteRange.value;

  const chartSetting = normalizedChartSetting.value;

  let { mediaTypes } = props;

  if (
    isChartSettingWithStreamsAndMediaTypes(chartSetting) &&
    chartSetting.types?.length
  ) {
    mediaTypes = chartSetting.types;
  }

  const params: ChartLibraryRequestParams = {
    chart_type: props.chartConfig.method,
    chart_variant: chartSetting.chart_variant,
    size: props.chartConfig.options?.size || 0,
    before: chartRange.before,
    after: chartRange.after,
    time_zone: currentUser.value.time_zone,
    interval: getChartInterval(chartRange, chartSetting.interval),
    options: {
      ...transformKeys(props.chartConfig.options ?? {}, "snakeCase"),
      beta: betaEnabled.value,
      collapse_syndicated: props.collapseSyndicated,
      ...(isChartSettingWithStockSymbol(chartSetting) &&
      chartSetting.stock_symbol
        ? {
            stock_symbol: chartSetting.stock_symbol,
          }
        : {}),
    },
    advanced_filters: hasIntentionalEmptyStreams.value
      ? null
      : serializeAdvancedFilters(chartSetting.advanced_filters),
    queries: getChartQueries(),
    linked_stream_ids: linkedStreamIds.value,
    media_release_ids: mediaReleaseIds.value,
    media_release_id:
      props.selectedMediaRelease?.id ?? mediaReleaseIds.value?.[0],
    insights_metrics: insightsMetrics.value,
    instant_insights_report_id: props.instantInsightsReportId,
    is_public_insights: isPublic,
    uuid: props.chartSetting.uuid,
  };

  if (isChartSettingWithAnalyticsIntegration(props.chartSetting)) {
    params.options.analytics_integration_target =
      props.chartSetting.analytics_integration_target;
  }

  if (
    isChartSettingWithStreamComparison(props.chartSetting) ||
    isChartSettingWithMentionStreamsAndSocialStreams(props.chartSetting)
  ) {
    params.options.stream_ids = comparisonStreamIds.value;
  }

  if (isChartSettingWithSortBy(props.chartSetting)) {
    params.sort_by = props.chartSetting.sort_by;
  }

  if (isChartSettingWithExportOptions(props.chartSetting)) {
    params.export_options = props.chartSetting.export_options;
  }

  if (isChartSettingWithOutreachActivityFilters(props.chartSetting)) {
    params.outreach_activity_filters = transformKeys(
      props.chartSetting.outreach_activity_filters,
      "snakeCase"
    );

    if (features.has("has_team_workspaces") && selectedTeamPreference.value) {
      params.organisation_team_id = selectedTeamPreference.value;
    }
  }

  if (
    isChartSettingWithSocialEngagePerformanceMetrics(props.chartSetting) ||
    isChartSettingWithSocialAccountSelection(props.chartSetting)
  ) {
    params.organisation_social_integration_ids =
      props.chartSetting.organisation_social_integration_ids;
  }

  if (isChartSettingWithInstagramHandleSelection(props.chartSetting)) {
    params.instagram_handles_organisation_social_integration_ids =
      props.chartSetting.instagram_handles_organisation_social_integration_ids;
  }

  if (isChartSettingWithPostSelection(props.chartSetting)) {
    params.post_ids = props.chartSetting.post_ids;
  }

  if (props.media.length) {
    params.media = props.media;

    // When advanced_filters are present, media must also be set inside them
    // so that click-through requests stay in sync with the chart's media scope.
    if (params.advanced_filters) {
      params.advanced_filters = {
        ...params.advanced_filters,
        media: params.media,
      };
    }
  }

  if (mediaTypes.length) {
    params.types = mediaTypes;
  }

  if (props.authors.length) {
    params.authors = props.authors.join(",");
  }

  if (props.filterSetId) {
    params.filter_set_id = props.filterSetId;
  }

  if (isChartSettingWithSovMetricType(props.chartSetting)) {
    params.metric_type = props.chartSetting.metric_type;
  }

  if (isChartSettingWithAIVisibility(props.chartSetting)) {
    if (props.chartSetting.exclude_llm) {
      params.options.exclude_llm = true;
    }

    if (llmModels.value.length) {
      params.options.sources = llmModels.value;
    }

    if (llmEntities.value.length) {
      params.options.entity_filters = {
        brands: llmEntities.value,
      };
    }
  }

  if (isChartSettingWithContactKeys(props.chartSetting)) {
    if (props.chartSetting.show_contacts_only) {
      params.options.show_contacts_only = true;
    }
  }

  Object.assign(params.options, props.chartConfig.chartOptions ?? {});

  return params;
}

function paginateData(): void {
  chartData.value = cloneDeep(copyChartData.value);

  if (!chartData.value.data) {
    return;
  }

  if (!isChartLibraryData(chartData.value.data)) {
    return;
  }

  const { page, pageSize } = pagination.value;

  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  if (props.chartConfig.component === "TopPlatformPosts") {
    chartData.value.data.top_platform_posts = (
      chartData.value.data.top_platform_posts ?? []
    ).slice(start, end);

    return;
  }

  if (chartData.value.data.columns?.length) {
    // ChartLibraryData is an intersection whose members eslint keeps in
    // alphabetical order, so `columns` resolves against whichever member sorts
    // first. Cast the read as well as the write, otherwise the element type
    // silently follows the sort rather than the intent.
    const columns = chartData.value.data
      .columns as ChartLibraryTableDataColumn[];

    (chartData.value.data.columns as ChartLibraryTableDataColumn[]) =
      columns.map((column) => ({
        ...column,
        rows: column.rows.slice(start, end),
      }));
  }
}

function setupPagination(data: ChartLibraryProps): void {
  const chartLibraryData = data.data as ChartLibraryData | undefined;

  if (props.chartConfig.component === "TopPlatformPosts") {
    numberOfRecords.value = chartLibraryData?.top_platform_posts?.length || 0;
  } else {
    numberOfRecords.value = chartLibraryData?.columns?.[0]?.rows?.length || 0;
  }

  if (props.displayPagination) {
    copyChartData.value = cloneDeep(data);
    pagination.value.pageCount = Math.ceil(numberOfRecords.value / PAGE_SIZE);
    paginateData();
  }
}

async function load(): Promise<void> {
  if (
    loaded.value ||
    abortController.value ||
    !isIntersecting.value ||
    props.disableLoading ||
    isUnmounted.value
  ) {
    return;
  }

  const controller = new AbortController();
  abortController.value = controller;

  try {
    error.value = false;
    loading.value = true;

    const params = prepareParams();

    const cacheKey = await makeCacheKey([
      selectedStreamId.value,
      params,
      getLocale(),
      props.chartSetting.uuid,
    ]);

    if (controller.signal.aborted) return;

    const response = await withCachedResults(
      cacheKey,
      () =>
        insightsService.streamInsights(selectedStreamId.value, {
          params,
          signal: controller.signal,
        }),
      shouldSkipCache.value
    );

    if (controller.signal.aborted) return;

    [chartData.value] = response.data;

    if (chartData.value.options && props.disableAnimation) {
      chartData.value.options.animation = false;
    }

    setupPagination(chartData.value);

    emit("data-loaded", chartData.value);
    loaded.value = true;
    firstLoad.value = false;
  } catch (loadError: unknown) {
    if (controller.signal.aborted || isAbortError(loadError)) {
      return;
    }

    loaded.value = true;
    firstLoad.value = false;
    error.value = true;
  } finally {
    if (abortController.value === controller) {
      abortController.value = undefined;
      loading.value = firstLoad.value;
    }
  }
}

const debouncedLoad = computed(() => debounceHandler(load, props.debounce));

function cancelExistingLoadRequest(): void {
  if (debouncedLoad.value?.cancel) {
    debouncedLoad.value.cancel();
  }

  if (abortController.value) {
    abortController.value.abort();
    abortController.value = undefined;
  }
}

function reload(): void {
  loaded.value = false;
  pagination.value.page = 1;
  cancelExistingLoadRequest();
  debouncedLoad.value();
}

onMounted(() => {
  if (props.initialChartData) {
    setupPagination(props.initialChartData);
  }

  load();
});

onBeforeUnmount(() => {
  isUnmounted.value = true;
  cancelExistingLoadRequest();
});

watch(() => selectedDashboard?.value, reload);

watch(pagination, paginateData, { deep: true });

watch(
  () => props.selectedStream,
  (newStream, oldStream) => {
    if (
      isSameStream(newStream, oldStream) &&
      props.selectedStream?.onlyLastSeenUpdated
    )
      return;

    reload();
  },
  { deep: true }
);

watch(
  () => props.filterSetId,
  (newFilterSetId, oldFilterSetId) => {
    if (newFilterSetId === oldFilterSetId) return;

    reload();
  }
);

watch(
  () => props.selectedMediaRelease,
  (newRelease, oldRelease) => {
    if (newRelease?.id === oldRelease?.id) return;

    reload();
  },
  { deep: true }
);

watch(
  () => props.streams,
  (newStreams, oldStreams) => {
    if (
      isChartSettingWithLinkedSocialStreams(props.chartSetting) &&
      !isEqual(oldStreams, newStreams)
    )
      reload();
  },
  { deep: true }
);

watch(
  () => props.range,
  (newRange, oldRange) => {
    if (
      newRange.before !== oldRange.before ||
      newRange.after !== oldRange.after
    ) {
      reload();
    }
  }
);

watch(
  () => props.media,
  (newMedia, oldMedia) => {
    if (JSON.stringify(newMedia) !== JSON.stringify(oldMedia)) {
      reload();
    }
  },
  { deep: true }
);

watch(
  () => props.mediaTypes,
  (newTypes, oldTypes) => {
    if (JSON.stringify(newTypes) !== JSON.stringify(oldTypes)) {
      reload();
    }
  },
  { deep: true }
);

watch(
  () => props.chartConfig,
  (newConfig, oldConfig) => {
    if (JSON.stringify(newConfig) !== JSON.stringify(oldConfig)) {
      reload();
    }
  }
);

watch(
  () => props.chartSetting,
  (newSetting, oldSetting) => {
    if (JSON.stringify(newSetting) !== JSON.stringify(oldSetting)) {
      reload();
    }
  }
);

watch(() => props.collapseSyndicated, reload);

watch(isIntersecting, (intersecting) => {
  if (intersecting && !loaded.value) {
    debouncedLoad.value();
  } else {
    emit("close-popup", chart.value!);
    debouncedLoad.value.cancel();
  }
});

watch(
  () => props.disableLoading,
  (loadingDisabled) => {
    if (
      !loadingDisabled &&
      !loaded.value &&
      (firstLoad.value || !loading.value)
    ) {
      debouncedLoad.value();
    }
  }
);

watch(
  () => props.hasAiVisibilityProject,
  () => {
    if (props.hasAiVisibilityProject) {
      loading.value = true;
      loaded.value = false;
      firstLoad.value = true;
    }
  },
  { immediate: true }
);

watch(chartQueries, reload, { deep: true });

watch(linkedStreamIds, reload, { deep: true });

watch(selectedTeamPreference, (teamPreference, previousTeamPreference) => {
  if (!features.has("has_team_workspaces")) {
    return;
  }

  if (!isChartSettingWithOutreachActivityFilters(props.chartSetting)) {
    return;
  }

  if (teamPreference !== previousTeamPreference) {
    reload();
  }
});

watch(
  () => currentUser.value.currency_code,
  () => {
    if (props.chartSetting.chart === "advertising_value_equivalency") {
      reload();
    }
  }
);

watch(
  () => props.cacheToken,
  () => {
    firstLoad.value = true;
    reload();
  }
);

defineExpose({
  getChartInterval,
  loading,
});
</script>

<style lang="scss" scoped>
.bottom-border-divider {
  border-bottom: 1px solid $hover-background;
}
</style>
